// MARK://dhk8/admin-shell/components/StatCard
// Tarjeta KPI: label arriba, value grande (en --font-mono / tabular si es numérico),
// delta opcional con color de tono. `SummaryStrip` acomoda una fila responsive de
// StatCards. Estado/realce por color de texto y tint, sin edge-strips. Tokens del
// sistema.
import type { ReactNode } from 'react'

export type StatTone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral'

export interface StatCardProps {
  label: string
  value: ReactNode
  /** Variación opcional (ej. "+12%", "-3"). Coloreada según `tone`. */
  delta?: string
  /** Tono del delta. Default 'neutral'. */
  tone?: StatTone
}

const TONE_FG: Record<StatTone, string> = {
  ok: 'var(--pos-success)',
  warn: 'var(--pos-warning)',
  danger: 'var(--pos-danger)',
  info: 'var(--pos-info)',
  neutral: 'var(--pos-fg-tertiary)',
}

// Heurística: si el value (o su trim) parece numérico (dígitos, separadores,
// símbolos de moneda/%), lo renderizamos con fuente tabular para alinear cifras.
function looksNumeric(value: ReactNode): boolean {
  if (typeof value === 'number') return true
  if (typeof value === 'string') return /^[\s$€£¥+\-]*[\d][\d.,%\s$€£¥xX]*$/.test(value.trim())
  return false
}

export default function StatCard({ label, value, delta, tone = 'neutral' }: StatCardProps) {
  const numeric = looksNumeric(value)
  return (
    <div className="nui-statcard">
      <span className="nui-statcard__label">{label}</span>
      <span className="nui-statcard__value" data-numeric={numeric}>{value}</span>
      {delta !== undefined && (
        <span className="nui-statcard__delta" style={{ color: TONE_FG[tone] }}>{delta}</span>
      )}
      <style>{`
        .nui-statcard {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: var(--pos-space-4, 16px);
          background: var(--pos-surface);
          border: 1px solid var(--pos-separator-strong);
          border-radius: var(--pos-radius-lg, 12px);
          box-shadow: var(--pos-shadow-1);
          min-width: 0;
        }
        .nui-statcard__label {
          font-size: var(--pos-text-xs, 12px);
          font-weight: var(--pos-weight-medium, 500);
          color: var(--pos-fg-tertiary);
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }
        .nui-statcard__value {
          font-family: var(--font-display, var(--font-sans));
          font-size: var(--pos-text-2xl, 24px);
          font-weight: var(--pos-weight-bold, 700);
          color: var(--pos-fg);
          line-height: var(--pos-leading-tight, 1.15);
        }
        .nui-statcard__value[data-numeric="true"] {
          font-family: var(--font-mono, var(--font-sans));
          font-variant-numeric: tabular-nums;
          letter-spacing: var(--pos-tracking-snug, -0.005em);
        }
        .nui-statcard__delta {
          font-size: var(--pos-text-xs, 12px);
          font-weight: var(--pos-weight-semibold, 600);
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </div>
  )
}

export interface SummaryStripProps {
  items: StatCardProps[]
}

export function SummaryStrip({ items }: SummaryStripProps) {
  return (
    <div className="nui-summary-strip">
      {items.map((it, i) => <StatCard key={i} {...it} />)}
      <style>{`
        /* Lado a lado por defecto; solo se apilan cuando NO caben (Gustavo 2026-06-23).
           Antes era un grid auto-fit que, al ser hijo de un contenedor flex sin ancho
           explícito, se encogía a su contenido y colapsaba a 1 columna (apilado). */
        .nui-summary-strip {
          display: flex;
          flex-wrap: wrap;
          gap: var(--pos-space-3, 12px);
          width: 100%;
        }
        .nui-summary-strip > .nui-statcard {
          flex: 1 1 200px;
          max-width: 360px;
        }
      `}</style>
    </div>
  )
}
